import React from 'react';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
    termsContainer: {
        padding: '20px',
        maxWidth: '800px',
        margin: '0 auto',
        fontFamily: 'Arial, sans-serif',
        color: '#333',
        lineHeight: 1.6,
    },
    termsTitle: {
        fontSize: '2.5em',
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#1b2a4e',
        marginBottom: '30px',
    },
    termsSection: {
        marginBottom: '30px',
    },
    appNotice: {
        backgroundColor: '#f4f7f9',
        padding: '20px',
        borderRadius: '8px',
        borderLeft: '5px solid #1b2a4e',
        marginBottom: '30px',
    },
    termsSubtitle: {
        fontSize: '1.5em',
        fontWeight: 'bold',
        color: '#1b2a4e',
        marginBottom: '10px',
    },
    termsText: {
        margin: '5px 0',
        textAlign: 'justify',
    },
    indent: {
        marginLeft: '1rem',
        display: 'block'
    },
});

const PrivacyPolicyEN: React.FC = () => {
    const classes = useStyles();

    return (
        <div className={classes.termsContainer}>
            <h1 className={classes.termsTitle}>Privacy Policy</h1>

            <section className={classes.appNotice}>
                <h2 className={classes.termsSubtitle}>Addendum: Mobile Application Data Usage</h2>
                <p className={classes.termsText}>
                    As part of the clinical collaboration with <b>Parc Taulí</b> for the study and treatment of depression, the DigimEvo mobile application requires the processing of specific data to enable its healthcare functions:
                </p>
                <p className={classes.termsText}>
                    <span className={classes.indent}>
                        • <b>Background Location:</b> We collect precise location data (GPS/Geofencing) even when the app is closed or not in use. This data is used exclusively to monitor patient mobility and detect clinical indicators of anhedonia or social isolation, enabling early alerts for medical staff.
                    </span>
                    <span className={classes.indent}>
                        • <b>Device Usage Access:</b> We monitor usage patterns of installed applications to evaluate the patient's digital well-being as a biomarker of their mood state.
                    </span>
                    <span className={classes.indent}>
                        • <b>Third-Party Transfer:</b> The aforementioned data is processed by DigimEvo and transferred to our collaborating technology partners (including <b>Evidenze</b>) under strict security and anonymization protocols, for the sole purpose of providing clinical statistics to the responsible physician.
                    </span>
                </p>
            </section>

            <p className={classes.termsText}>
                DigimEvo takes your privacy very seriously and handles all your personal data with great care. This document sets out DigimEvo's policy regarding privacy and security. You are advised to read this policy carefully. Capitalized words are defined in the Terms of Service.
            </p>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>1. Who is DigimEvo?</h2>
                <p className={classes.termsText}>
                    DigimEvo Limited is a limited liability company incorporated and existing under the laws of Spain, with its registered office at Travessera de les Corts 262, 12 2, Barcelona 08014.
                </p>
                <p className={classes.termsText}>
                    DigimEvo has developed a platform used by healthcare providers to collect information from and/or provide information to their patients. DigimEvo is not a healthcare provider and does not filter the content posted by healthcare providers, nor does it select or filter specific information displayed to patients.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>2. Applicable Law</h2>
                <p className={classes.termsText}>
                    DigimEvo Limited, as a company incorporated in Spain, is subject to the Data Protection Act 2018, Regulation (EU) 679/2016 (GDPR), and the Privacy and Electronic Communications Regulations 2011 when controlling or processing data in its business.
                </p>
                <p className={classes.termsText}>
                    However, where you live and where your healthcare provider is incorporated may affect the privacy and data protection laws that apply to the control or processing of your data.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>3. DigimEvo as a Data Processor</h2>
                <p className={classes.termsText}>
                    DigimEvo will store and process personal data on behalf of its clients, the healthcare providers. For this processing, the healthcare provider will act as the "data controller" within the meaning of the Data Protection Act 2018 and Regulation (EU) 679/2016 (GDPR).
                </p>
                <p className={classes.termsText}>
                    DigimEvo also sometimes acts as a data processor when it is responsible for the lawful processing of personal data. The legal grounds on which we process personal data may vary depending on the terms of our business, but generally, we rely on a variety of legal bases to process, including contract, consent, and legitimate expectation. Please consult your healthcare provider, the data controller, to understand how they may process your personal data and the terms of their privacy policies.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>4. DigimEvo as a Data Controller</h2>
                <p className={classes.termsText}>
                    In certain circumstances, DigimEvo may also process your personal data for its own purposes, in which case DigimEvo will be the "data controller" of your personal data and responsible for the lawful processing of this personal data. You may object to your data being processed; however, this may prevent us from providing you with our services. Please contact us using the contact details below if you wish us to stop processing your data.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>5. What Personal Data Does DigimEvo Collect and Process?</h2>
                <p className={classes.termsText}>
                    To use the Service, you need to create a personal account. For this purpose, you must enter certain information about yourself. Your name, gender, and email address are required; other fields are optional, such as phone number or country of residence.
                </p>
                <p className={classes.termsText}>
                    The information contained in your account is not visible to third parties. For patients, only the healthcare provider authorized by the patient can see the information in their account.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>6. Terms of Service</h2>
                <p className={classes.termsText}>
                    By using the Service, the healthcare provider shares educational information through video prescription with patients, and patients provide feedback on it. This information is private between the patient and the healthcare provider and is only accessible if the patient authorizes the healthcare provider to access the data.
                </p>
                <p className={classes.termsText}>
                    DigimEvo stores this information on behalf of the healthcare provider. DigimEvo will only process patient information for its own purposes with the patient's consent. Once the patient has given consent, DigimEvo will anonymize the patient's information and share it with authorized third parties.
                </p>
                <p className={classes.termsText}>
                    If the patient is a minor, the patient's parents or legal guardians will be asked to give their consent for the processing described above.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>7. Cookies</h2>
                <p className={classes.termsText}>
                    When you use DigimEvo, cookies are saved on your computer. Cookies are small pieces of information (in text form) that a server sends to your browser (such as Internet Explorer or Firefox) intending for the browser to send this information to the server the next time a user uses the Service. Cookies cannot harm your computer or the files stored on it.
                </p>
                <p className={classes.termsText}>
                    When you use the Service, first-party cookies are saved on your computer. First-party cookies are created by or for DigimEvo, and DigimEvo stores them on your computer, and only DigimEvo has access to these cookies. DigimEvo uses these cookies, for example, to remember your login information.
                </p>
                <p className={classes.termsText}>
                    To collect data about the use of the DigimEvo website (the marketing website, not the platform used to access the Service), DigimEvo uses Google Analytics. Google Analytics stores a persistent cookie on your computer, which is subsequently used to record your use of the website. Google then analyzes this data and the results are provided to DigimEvo. This allows DigimEvo to improve its services to customers and website visitors.
                </p>
                <p className={classes.termsText}>
                    You can configure your browser not to receive cookies the next time you use the Service. However, you may no longer be able to make full use of the DigimEvo website or the online portal login services offered.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>8. Purposes for Which DigimEvo Processes Personal Data</h2>
                <p className={classes.termsText}>
                    DigimEvo may use your personal data for the following purposes:
                    <br />
                    <span className={classes.indent}>- To enable the healthcare provider to use the Service, including managing video prescription programs, patient compliance, and template sharing.</span>
                    <span className={classes.indent}>- To enable the patient to use the Service, including accessing home programs and providing feedback.</span>
                    <span className={classes.indent}>- To communicate with you about the Service and/or other DigimEvo services.</span>
                    <span className={classes.indent}>- To set up DigimEvo for your use.</span>
                    <span className={classes.indent}>- For protection purposes and to generate anonymous statistical data.</span>
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>9. Data Sharing</h2>
                <p className={classes.termsText}>
                    DigimEvo will only disclose medical information to third parties when the healthcare provider has consented for the specific third party involved and if the information is anonymous.
                    <br />
                    DigimEvo will only provide your personal data to third parties other than those set out in this Privacy Policy in the following cases:
                    <br />
                    <span className={classes.indent}>- If required to do so based on the agreement with the healthcare provider.</span>
                    <span className={classes.indent}>- If required to do so under national or international laws, case law, and/or regulations.</span>
                    <span className={classes.indent}>- If DigimEvo deems it necessary to defend its own rights.</span>
                    <span className={classes.indent}>- If you have given permission to do so.</span>
                    <br />
                    DigimEvo may publish customer testimonials, comments, or reviews with the healthcare provider's consent.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>10. International Data Transfers</h2>
                <p className={classes.termsText}>
                    Since DigimEvo uses localized servers, this minimizes data transfers to countries without adequate legal protection. For a complete list of "adequate" countries approved by the European Commission, visit the EC official website.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>11. Behavioral Data Processing</h2>
                <p className={classes.termsText}>
                    DigimEvo uses third-party analytics services to help understand service usage. No patient information is shared through these services.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>12. Data Retention</h2>
                <p className={classes.termsText}>
                    All data will be retained in accordance with the Data Retention Policy. When a service is cancelled, data will be deleted or retained as per the policy and legal obligations.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>13. Data Security</h2>
                <p className={classes.termsText}>
                    To protect the confidentiality and integrity of your personal data, we have:
                    <br />
                    <span className={classes.indent}>- Internal policies for privacy and confidentiality.</span>
                    <span className={classes.indent}>- Encryption of all communications (SSL/TLS).</span>
                    <span className={classes.indent}>- Encryption of patient health information "at rest" in our database.</span>
                    <span className={classes.indent}>- Access limitation to the minimum necessary personnel.</span>
                    <span className={classes.indent}>- Use of firewalls, secure data centers, and world-class CDN filtering.</span>
                    <span className={classes.indent}>- Automatic session timeouts and secure password requirements.</span>
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>14. Your Right to Access or Delete Your Personal Data</h2>
                <p className={classes.termsText}>
                    You may request access to, a copy of, or changes to your personal data by contacting gdpr@digimevo.com. We will respond within 4 weeks. Account deletion requests are subject to legal retention obligations.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>15. Relevant Supervisory Authority</h2>
                <p className={classes.termsText}>
                    If you have a complaint, please contact your healthcare provider first. You also have the right to lodge a complaint with your national supervisory authority. A full list of European bodies is available via the EDPB website.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>16. Policy Date</h2>
                <p className={classes.termsText}>
                    This policy is subject to annual review. Any changes will be posted on the DigimEvo website. This policy was last updated on March 9, 2026.
                </p>
            </section>

            <section className={classes.termsSection}>
                <h2 className={classes.termsSubtitle}>17. Contact Information</h2>
                <p className={classes.termsText}>
                    If you have any questions, please contact our data protection officer at gdpr@digimevo.com or at Travessera de les Corts 262, 12 2, esc B, 08014 Barcelona, Spain.
                </p>
            </section>
        </div>
    );
};

export default PrivacyPolicyEN;